import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  FlexColumn, 
  FlexRow, 
  Text, 
  Button, 
  Input, 
  TextArea,
  Select,
  Spacer
} from '../../../styles/StyledComponents';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../../styles/ThemeStyles';
import { 
  User, 
  MapPin, 
  Package, 
  ShoppingCart, 
  Search, 
  Filter, 
  Plus, 
  Minus, 
  CheckCircle, 
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Star,
  Heart,
  Eye,
  EyeOff
} from 'lucide-react';

// Enhanced Form Container with better spacing and visual hierarchy
export const OptimizedFormContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0;
    padding: 0;
  }
`;

// Enhanced Form Section with better visual design
export const OptimizedFormSection = styled(Card)`
  margin-bottom: ${Spacing.lg};
  padding: ${Spacing.md};
  border-radius: ${BorderRadius.md};
  box-shadow: ${Shadows.sm};
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    box-shadow: ${Shadows.md};
  }
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm};
    margin-bottom: ${Spacing.md};
  }
`;

// Enhanced Section Title with better typography and modern design
export const OptimizedSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.xl};
  padding: ${Spacing.lg};
  background: linear-gradient(135deg, #ffffff 0%, #f8fffe 100%);
  border-radius: ${BorderRadius.lg};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(76, 175, 80, 0.08);
  border-left: 4px solid ${ThemeColors.primary.main};
  
  .icon {
    background: linear-gradient(135deg, ${ThemeColors.primary.light}, ${ThemeColors.primary.main});
    color: white;
    padding: ${Spacing.sm};
    border-radius: ${BorderRadius.md};
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .title {
    font-size: ${Typography.fontSize.xl};
    font-weight: ${Typography.fontWeight.bold};
    color: ${ThemeColors.text.primary};
    margin: 0;
    font-family: ${Typography.fontFamily.secondary};
  }
  
  .subtitle {
    font-size: ${Typography.fontSize.sm};
    color: ${ThemeColors.text.secondary};
    margin-top: ${Spacing.xs};
    font-style: italic;
    font-weight: ${Typography.fontWeight.medium};
  }
`;

// Enhanced Form Grid with better responsive behavior
export const OptimizedFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${Spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.md};
  }
`;

// Enhanced Field Container with better spacing and validation states
export const OptimizedFieldContainer = styled.div<{ hasError?: boolean; isFocused?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  
  .field-label {
    font-size: ${Typography.fontSize.sm};
    font-weight: ${Typography.fontWeight.semiBold};
    color: ${ThemeColors.text.primary};
    display: flex;
    align-items: center;
    gap: ${Spacing.xs};
    
    .required {
      color: ${ThemeColors.error.main};
    }
  }
  
  .field-input {
    position: relative;
    
    .input-icon {
      position: absolute;
      left: ${Spacing.sm};
      top: 50%;
      transform: translateY(-50%);
      color: ${ThemeColors.text.secondary};
      z-index: 1;
    }
    
    .input-field {
      width: 100%;
      padding: ${Spacing.sm};
      padding-left: 40px;
      border: 2px solid ${props => 
        props.hasError ? ThemeColors.error.main : 
        props.isFocused ? ThemeColors.primary.main : 
        ThemeColors.border.light};
      border-radius: ${BorderRadius.sm};
      font-size: ${Typography.fontSize.md};
      transition: all 0.3s ease;
      background: white;
      
      &:focus {
        outline: none;
        border-color: ${ThemeColors.primary.main};
        box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
      }
      
      &:hover:not(:focus) {
        border-color: ${ThemeColors.primary.light};
      }
      
      &::placeholder {
        color: ${ThemeColors.text.secondary};
      }
      
      &:disabled {
        background-color: ${ThemeColors.gray[50]};
        color: ${ThemeColors.text.secondary};
        cursor: not-allowed;
      }
      
      &.autopopulated {
        background-color: ${ThemeColors.gray[50]};
        color: ${ThemeColors.text.secondary};
        cursor: not-allowed;
        border-color: ${ThemeColors.gray[200]};
        
        &:focus {
          border-color: ${ThemeColors.gray[200]};
          box-shadow: none;
        }
        
        &:hover:not(:focus) {
          border-color: ${ThemeColors.gray[200]};
        }
      }
    }
  }
  
  .field-error {
    display: flex;
    align-items: center;
    gap: ${Spacing.xs};
    color: ${ThemeColors.error.main};
    font-size: ${Typography.fontSize.sm};
    margin-top: ${Spacing.xs};
  }
  
  .field-help {
    color: ${ThemeColors.text.secondary};
    font-size: ${Typography.fontSize.sm};
    margin-top: ${Spacing.xs};
  }
  
  .radio-group {
    display: flex;
    gap: 30px;
    margin-top: ${Spacing.sm};
    
    .radio-option {
      display: flex;
      align-items: center;
      gap: ${Spacing.sm};
      cursor: pointer;
      padding: ${Spacing.sm} ${Spacing.md};
      border: 2px solid ${ThemeColors.border.light};
      border-radius: ${BorderRadius.md};
      transition: all 0.3s ease;
      background: white;
      
      &:hover {
        background: ${ThemeColors.primary.light};
        border-color: ${ThemeColors.primary.main};
      }
      
      input[type="radio"] {
        width: 18px;
        height: 18px;
        accent-color: ${ThemeColors.primary.main};
        cursor: pointer;
      }
      
      input[type="radio"]:checked + .radio-label {
        color: ${ThemeColors.primary.main};
        font-weight: ${Typography.fontWeight.semiBold};
      }
      
      .radio-label {
        font-size: ${Typography.fontSize.md};
        color: ${ThemeColors.text.primary};
        cursor: pointer;
      }
    }
  }
`;

// Enhanced Product Grid with better card design
export const OptimizedProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${Spacing.md};
  margin-top: ${Spacing.md};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: ${Spacing.sm};
  }
`;

// Enhanced Product Card with modern design and better interactions
export const OptimizedProductCard = styled(Card)<{ isSelected?: boolean }>`
  padding: ${Spacing.lg};
  border: 2px solid ${props => 
    props.isSelected ? ThemeColors.primary.main : 
    'rgba(76, 175, 80, 0.1)'};
  border-radius: ${BorderRadius.lg};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: ${props => 
    props.isSelected ? 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)' : 
    'linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)'};
  box-shadow: ${props => props.isSelected 
    ? '0 8px 24px rgba(76, 175, 80, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)' 
    : '0 4px 16px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)'};
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.isSelected 
      ? 'linear-gradient(90deg, #4CAF50, #388E3C)' 
      : 'transparent'};
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.isSelected 
      ? '0 12px 32px rgba(76, 175, 80, 0.2), 0 6px 16px rgba(0, 0, 0, 0.1)' 
      : '0 8px 24px rgba(76, 175, 80, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06)'};
    border-color: ${props => props.isSelected 
      ? ThemeColors.primary.dark 
      : 'rgba(76, 175, 80, 0.2)'};
  }
  
  &:active {
    transform: translateY(0);
    transition: transform 0.1s ease;
  }
  
  
  .product-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: ${Spacing.md};
    
    .product-title {
      font-weight: ${Typography.fontWeight.semiBold};
      color: ${ThemeColors.text.primary};
      flex: 1;
      line-height: 1.4;
      font-size: ${Typography.fontSize.lg};
      margin-right: ${Spacing.sm};
    }
    
    .product-price {
      font-weight: ${Typography.fontWeight.bold};
      color: ${ThemeColors.primary.main};
      font-size: ${Typography.fontSize.xl};
      display: flex;
      align-items: center;
      gap: ${Spacing.xs};
      
      &::before {
        content: '$';
        font-size: ${Typography.fontSize.sm};
        opacity: 0.7;
      }
    }
  }
  
  .product-code {
    font-size: ${Typography.fontSize.sm};
    color: ${ThemeColors.text.secondary};
    margin-bottom: ${Spacing.sm};
    font-family: 'Courier New', monospace;
    background: rgba(76, 175, 80, 0.1);
    padding: ${Spacing.xs} ${Spacing.sm};
    border-radius: ${BorderRadius.sm};
    display: inline-block;
    font-weight: ${Typography.fontWeight.medium};
  }
  
  .product-category {
    display: inline-block;
    background: linear-gradient(135deg, ${ThemeColors.primary.light}, ${ThemeColors.primary.main});
    color: white;
    padding: ${Spacing.xs} ${Spacing.sm};
    border-radius: ${BorderRadius.sm};
    font-size: ${Typography.fontSize.xs};
    font-weight: ${Typography.fontWeight.semiBold};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: ${Spacing.md};
    box-shadow: 0 2px 6px rgba(76, 175, 80, 0.2);
  }
  
  .quantity-controls {
    display: flex;
    align-items: center;
    gap: ${Spacing.sm};
    margin-top: ${Spacing.sm};
    
    .quantity-label {
      font-size: ${Typography.fontSize.sm};
      font-weight: ${Typography.fontWeight.medium};
      color: ${ThemeColors.text.primary};
    }
    
    .quantity-input-group {
      display: flex;
      align-items: center;
      border: 2px solid ${ThemeColors.border.light};
      border-radius: ${BorderRadius.sm};
      overflow: hidden;
      
      .quantity-button {
        background: ${ThemeColors.primary.main};
        color: white;
        border: none;
        padding: ${Spacing.xs};
        cursor: pointer;
        transition: all 0.3s ease;
        
        &:hover {
          background: ${ThemeColors.primary.dark};
        }
        
        &:disabled {
          background: ${ThemeColors.gray[300]};
          cursor: not-allowed;
        }
      }
      
      .quantity-input {
        border: none;
        padding: ${Spacing.xs} ${Spacing.sm};
        text-align: center;
        font-weight: ${Typography.fontWeight.semiBold};
        width: 72px;
        background: white;
        font-size: 16px; /* Prevent zoom on iOS - must be at least 16px */
        
        &:focus {
          outline: none;
        }
      }
    }
  }
`;

// Enhanced Order Summary with better visual design
export const OptimizedOrderSummary = styled(Card)`
  background: linear-gradient(135deg, ${ThemeColors.primary.light} 0%, ${ThemeColors.secondary.light} 100%);
  border: 2px solid ${ThemeColors.primary.main};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.lg};
  margin-top: ${Spacing.lg};
  position: sticky;
  top: ${Spacing.sm};
  
  .summary-header {
    display: flex;
    align-items: center;
    gap: ${Spacing.sm};
    margin-bottom: ${Spacing.md};
    
    .summary-icon {
      color: ${ThemeColors.primary.main};
      background: white;
      padding: ${Spacing.xs};
      border-radius: ${BorderRadius.sm};
    }
    
    .summary-title {
      font-size: ${Typography.fontSize.lg};
      font-weight: ${Typography.fontWeight.bold};
      color: ${ThemeColors.text.primary};
    }
  }
  
  .summary-items {
    max-height: 300px;
    overflow-y: auto;
    margin-bottom: ${Spacing.md};
    
    .summary-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: ${Spacing.xs} 0;
      border-bottom: 1px solid ${ThemeColors.border.light};
      
      &:last-child {
        border-bottom: none;
      }
      
      .item-name {
        font-weight: ${Typography.fontWeight.medium};
        color: ${ThemeColors.text.primary};
      }
      
      .item-price {
        font-weight: ${Typography.fontWeight.semiBold};
        color: ${ThemeColors.primary.main};
      }
    }
  }
  
  .summary-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: ${Spacing.sm};
    border-top: 2px solid ${ThemeColors.primary.main};
    font-size: ${Typography.fontSize.lg};
    font-weight: ${Typography.fontWeight.bold};
    color: ${ThemeColors.text.primary};
  }
`;

// Enhanced Submit Button with better visual design
export const OptimizedSubmitButton = styled(Button)`
  background: linear-gradient(135deg, ${ThemeColors.primary.main} 0%, ${ThemeColors.secondary.main} 100%);
  border: none;
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.md} ${Spacing.lg};
  font-size: ${Typography.fontSize.base};
  font-weight: ${Typography.fontWeight.bold};
  color: white;
  box-shadow: ${Shadows.md};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadows.lg};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: ${ThemeColors.gray[400]};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  .button-icon {
    margin-right: ${Spacing.xs};
  }
`;

// Search and Filter Components
export const SearchAndFilterContainer = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.md};
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const SearchInput = styled.div`
  flex: 1;
  min-width: 250px;
  position: relative;
  
  .search-icon {
    position: absolute;
    left: ${Spacing.sm};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
  }
  
  .search-field {
    width: 100%;
    padding: ${Spacing.sm};
    padding-left: 40px;
    border: 2px solid ${ThemeColors.border.light};
    border-radius: ${BorderRadius.sm};
    font-size: ${Typography.fontSize.md};
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: ${ThemeColors.primary.main};
      box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
    }
  }
`;

export const FilterSelect = styled.div`
  min-width: 200px;
  
  .filter-field {
    width: 100%;
    padding: ${Spacing.sm};
    border: 2px solid ${ThemeColors.border.light};
    border-radius: ${BorderRadius.sm};
    font-size: ${Typography.fontSize.md};
    background: white;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: ${ThemeColors.primary.main};
      box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
    }
  }
`;

// Collapsible Section Component
export const CollapsibleSection = styled.div<{ isOpen: boolean }>`
  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${Spacing.md};
    background: ${ThemeColors.primary.light};
    border-radius: ${BorderRadius.sm};
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: ${ThemeColors.primary.main};
      color: white;
    }
    
    .section-title {
      font-size: ${Typography.fontSize.base};
      font-weight: ${Typography.fontWeight.semiBold};
    }
    
    .section-icon {
      transition: all 0.3s ease;
      transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    }
  }
  
  .section-content {
    max-height: ${props => props.isOpen ? '1000px' : '0'};
    overflow: hidden;
    transition: all 0.3s ease;
    padding: ${props => props.isOpen ? Spacing.md : '0'};
    border: ${props => props.isOpen ? `1px solid ${ThemeColors.border.light}` : 'none'};
    border-top: none;
    border-radius: 0 0 ${BorderRadius.sm} ${BorderRadius.sm};
  }
`;

// Loading and Success States
export const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid ${ThemeColors.primary.light};
  border-radius: 50%;
  border-top-color: ${ThemeColors.primary.main};
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  background: ${ThemeColors.success.light};
  color: ${ThemeColors.success.main};
  border-radius: ${BorderRadius.sm};
  margin-bottom: ${Spacing.sm};
`;

export const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  background: ${ThemeColors.error.light};
  color: ${ThemeColors.error.main};
  border-radius: ${BorderRadius.sm};
  margin-bottom: ${Spacing.sm};
`;

// Export GlobalOrderSummary
export { GlobalOrderSummary } from './GlobalOrderSummary';

// Category Badge Component
export const CategoryBadge = styled.div<{ category: string; variant?: 'default' | 'outline' }>`
  display: inline-block;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  margin-bottom: ${Spacing.sm};
  
  ${props => {
    const variant = props.variant || 'default';
    const isOutline = variant === 'outline';
    
    switch (props.category) {
      case 'organic':
        return `
          background-color: ${isOutline ? 'transparent' : ThemeColors.success.light};
          color: ${ThemeColors.success.main};
          border: ${isOutline ? `1px solid ${ThemeColors.success.main}` : 'none'};
        `;
      case 'heritage':
        return `
          background-color: ${isOutline ? 'transparent' : ThemeColors.warning.light};
          color: ${ThemeColors.warning.main};
          border: ${isOutline ? `1px solid ${ThemeColors.warning.main}` : 'none'};
        `;
      case 'seedtape':
        return `
          background-color: ${isOutline ? 'transparent' : ThemeColors.info.light};
          color: ${ThemeColors.info.main};
          border: ${isOutline ? `1px solid ${ThemeColors.info.main}` : 'none'};
        `;
      case 'flower':
        return `
          background-color: ${isOutline ? 'transparent' : ThemeColors.secondary.light};
          color: ${ThemeColors.secondary.main};
          border: ${isOutline ? `1px solid ${ThemeColors.secondary.main}` : 'none'};
        `;
      case 'vegetable':
        return `
          background-color: ${isOutline ? 'transparent' : ThemeColors.success.light};
          color: ${ThemeColors.success.main};
          border: ${isOutline ? `1px solid ${ThemeColors.success.main}` : 'none'};
        `;
      case 'herb':
        return `
          background-color: ${isOutline ? 'transparent' : ThemeColors.info.light};
          color: ${ThemeColors.info.main};
          border: ${isOutline ? `1px solid ${ThemeColors.info.main}` : 'none'};
        `;
      default:
        return `
          background-color: ${isOutline ? 'transparent' : ThemeColors.gray[100]};
          color: ${ThemeColors.gray[600]};
          border: ${isOutline ? `1px solid ${ThemeColors.gray[600]}` : 'none'};
        `;
    }
  }}
`;

// Helper Components
export const SectionTitle: React.FC<{ 
  icon: React.ComponentType<any>; 
  title: string; 
  subtitle?: string; 
}> = ({ icon: Icon, title, subtitle }) => (
  <OptimizedSectionTitle>
    <div className="icon">
      <Icon size={24} />
    </div>
    <div>
      <div className="title">{title}</div>
      {subtitle && <div className="subtitle">{subtitle}</div>}
    </div>
  </OptimizedSectionTitle>
);

export const FieldContainer: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  help?: string;
  icon?: React.ComponentType<any>;
  children: React.ReactNode;
}> = ({ label, required, error, help, icon: Icon, children }) => (
  <OptimizedFieldContainer hasError={!!error}>
    <div className="field-label">
      {label}
      {required && <span className="required">*</span>}
    </div>
    <div className="field-input">
      {Icon && <Icon className="input-icon" size={18} />}
      {children}
    </div>
    {error && (
      <div className="field-error">
        <AlertCircle size={16} />
        {error}
      </div>
    )}
    {help && <div className="field-help">{help}</div>}
  </OptimizedFieldContainer>
);

export const QuantityControl: React.FC<{
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}> = ({ value, onChange, min = 0, max = 999 }) => {
  const [localValue, setLocalValue] = useState<string>(value.toString());
  
  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty string for clearing the field
    if (inputValue === '') {
      setLocalValue('');
      return;
    }
    
    // Parse the number
    const numValue = parseInt(inputValue, 10);
    
    // If it's a valid number, update both local and parent state
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(min, Math.min(max, numValue));
      setLocalValue(clampedValue.toString());
      onChange(clampedValue);
    } else {
      // If invalid, keep the local value but don't update parent
      setLocalValue(inputValue);
    }
  };

  const handleBlur = () => {
    // When field loses focus, ensure we have a valid number
    const numValue = parseInt(localValue, 10);
    if (isNaN(numValue) || localValue === '') {
      setLocalValue(min.toString());
      onChange(min);
    } else {
      const clampedValue = Math.max(min, Math.min(max, numValue));
      setLocalValue(clampedValue.toString());
      onChange(clampedValue);
    }
  };

  return (
    <div className="quantity-controls">
      <span className="quantity-label">Qty:</span>
      <div className="quantity-input-group">
        <button
          className="quantity-button"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          <Minus size={16} />
        </button>
        <input
          className="quantity-input"
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          min={min}
          max={max}
        />
        <button
          className="quantity-button"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
};
